// src/components/Sidebar.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/* ---------- Logo minimal ---------- */
function LogoFD({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <rect width="256" height="256" rx="28" fill="#0F1A2B" />
      <rect x="44" y="64" width="168" height="26" rx="13" fill="#CDD3DB" />
      <rect
        x="44"
        y="115"
        width="132"
        height="26"
        rx="13"
        fill="#CDD3DB"
        opacity="0.95"
      />
      <rect
        x="44"
        y="166"
        width="96"
        height="26"
        rx="13"
        fill="#CDD3DB"
        opacity="0.9"
      />
    </svg>
  );
}

/* ---------- Bouton lien réutilisable ---------- */
function NavItem({
  active,
  onClick,
  children,
  className = "",
  title,
  role = "button",
}) {
  const base = "p-2 rounded flex items-center gap-2 transition-colors text-sm";
  const activeCls = active
    ? "bg-slate-900 text-white font-semibold"
    : "text-slate-200 hover:bg-slate-800";
  return (
    <button
      type="button"
      role={role}
      onClick={onClick}
      title={title}
      className={`${base} ${activeCls} ${className}`}
    >
      {children}
    </button>
  );
}

/* ---------- Sous-menu réutilisable ---------- */
function Collapsible({
  id,
  label,
  open,
  onToggle,
  active,
  children,
  showCaret = true,
}) {
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
        className={`p-2 rounded w-full text-left flex items-center justify-between gap-2 transition-colors text-sm ${
          active
            ? "bg-slate-900 text-white font-semibold"
            : "text-slate-200 hover:bg-slate-800"
        }`}
      >
        <span className="flex items-center gap-2">{label}</span>
        {showCaret ? (
          <span
            aria-hidden
            className={`ml-2 inline-block transition-transform ${
              open ? "rotate-90" : ""
            }`}
          >
            ▸
          </span>
        ) : null}
      </button>

      <div
        id={id}
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {children}
      </div>
    </>
  );
}

/**
 * Sidebar principale FlowDevis
 * - Desktop : colonne fixe
 * - Mobile : drawer slide-in contrôlé par isMobileOpen / onMobileClose
 */
export default function Sidebar({
  isMobileOpen = false,
  onMobileClose,
  swipeProps = {}, // events de swipe (onTouchStart, etc.)
}) {
  const location = useLocation();
  const navigate = useNavigate();

  /* ---------- états UI ---------- */
  const [openDocs, setOpenDocs] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  /* ---------- chemins actifs ---------- */
  const pathname = location.pathname.toLowerCase();
  const actives = useMemo(() => {
    const docs =
      pathname.startsWith("/documents") ||
      pathname.startsWith("/documents-centre");
    return {
      home: pathname === "/",
      docs,
      devis: pathname.startsWith("/documents-centre/devis"),
      factures: pathname.startsWith("/documents-centre/factures"),
      clients: pathname.startsWith("/clients"),
      designations: pathname.startsWith("/designations"),
      personnaliser: pathname.startsWith("/personnaliser"),
      settingsProfile: pathname.startsWith("/parametres/profil"),
      settingsPlan: pathname.startsWith("/parametres/abonnement"),
    };
  }, [pathname]);

  /* ---------- auto-ouvrir selon la route ---------- */
  useEffect(() => {
    if (actives.docs) setOpenDocs(true);
  }, [actives.docs]);

  useEffect(() => {
    if (actives.settingsProfile || actives.settingsPlan) setOpenSettings(true);
  }, [actives.settingsProfile, actives.settingsPlan]);

  /* ---------- navigation avec garde + fermeture mobile ---------- */
  const go = useCallback(
    (to) => {
      const guard = window.__FLOWDEVIS_CONFIRM_LEAVE__;

      const doNav = () => {
        if (typeof onMobileClose === "function" && window.innerWidth < 1024) {
          onMobileClose();
        }
        navigate(to);
      };

      if (typeof guard === "function") {
        guard(doNav);
      } else {
        doNav();
      }
    },
    [navigate, onMobileClose]
  );

  const toggleDocs = useCallback(() => setOpenDocs((v) => !v), []);
  const toggleSettings = useCallback(() => setOpenSettings((v) => !v), []);

  return (
    <aside
      className={`
        bg-slate-900 text-white p-6 w-60 z-40
        fixed inset-y-0 left-0 transform transition-transform duration-200 ease-in-out
        lg:static lg:transform-none lg:flex lg:flex-col
        ${
          isMobileOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0"
        }
      `}
      aria-label="Navigation principale"
      {...swipeProps}
    >
      {/* Header logo + bouton fermer mobile */}
      <div className="mb-8 flex items-center justify-between">
        <span className="flex items-center gap-2 text-2xl font-bold">
          <span className="text-white">FlowDevis</span>
          <LogoFD size={32} />
        </span>

        {typeof onMobileClose === "function" && (
          <button
            type="button"
            onClick={onMobileClose}
            className="lg:hidden p-1 rounded hover:bg-slate-800"
            aria-label="Fermer le menu"
          >
            ✕
          </button>
        )}
      </div>

      <nav className="flex flex-col space-y-2">
        {/* Accueil */}
        <NavItem active={actives.home} onClick={() => go("/")} title="Accueil">
          <span>Accueil</span>
        </NavItem>

        {/* Devis & Factures */}
        <Collapsible
          id="submenu-docs"
          label="Devis & Factures"
          open={openDocs}
          onToggle={() => {
            go("/documents");
            toggleDocs();
          }}
          active={actives.docs}
          showCaret={false}
        >
          <div className="py-1">
            <NavItem
              role="menuitem"
              className="pl-8"
              active={actives.devis}
              onClick={() => {
                go("/documents-centre/devis");
                setOpenDocs(false);
              }}
            >
              <span className="opacity-80" aria-hidden="true">
                •
              </span>
              <span>Devis</span>
            </NavItem>
            <NavItem
              role="menuitem"
              className="pl-8"
              active={actives.factures}
              onClick={() => {
                go("/documents-centre/factures");
                setOpenDocs(false);
              }}
            >
              <span className="opacity-80" aria-hidden="true">
                •
              </span>
              <span>Factures</span>
            </NavItem>
          </div>
        </Collapsible>

        {/* Clients */}
        <NavItem
          active={actives.clients}
          onClick={() => go("/clients")}
          title="Clients"
        >
          <span>Clients</span>
        </NavItem>

        {/* Désignations */}
        <NavItem
          active={actives.designations}
          onClick={() => go("/designations")}
          title="Désignations"
        >
          <span>Désignations</span>
        </NavItem>

        {/* Personnaliser */}
        <NavItem
          active={actives.personnaliser}
          onClick={() => go("/personnaliser")}
          title="Personnaliser"
        >
          <span>Personnaliser</span>
        </NavItem>

        {/* Paramètres */}
        <Collapsible
          id="submenu-settings"
          label="⚙️ Paramètres"
          open={openSettings}
          onToggle={toggleSettings}
          active={actives.settingsProfile || actives.settingsPlan}
          showCaret={false}
        >
          <div className="py-1">
            <NavItem
              role="menuitem"
              className="pl-8"
              active={actives.settingsProfile}
              onClick={() => {
                go("/parametres/profil");
                setOpenSettings(true);
              }}
            >
              <span className="opacity-80" aria-hidden="true">
                •
              </span>
              <span>Mon profil</span>
            </NavItem>

            <NavItem
              role="menuitem"
              className="pl-8"
              active={actives.settingsPlan}
              onClick={() => {
                go("/parametres/abonnement");
                setOpenSettings(true);
              }}
            >
              <span className="opacity-80" aria-hidden="true">
                •
              </span>
              <span>Mon abonnement</span>
            </NavItem>

            <NavItem
              role="menuitem"
              className="pl-8"
              active={false}
              onClick={() => go("/logout")}
            >
              <span className="opacity-80" aria-hidden="true">
                •
              </span>
              <span>Se déconnecter</span>
            </NavItem>
          </div>
        </Collapsible>
      </nav>
    </aside>
  );
}
